import { inject, Injectable } from '@angular/core'

import { MessageService } from 'primeng/api'
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, takeUntil } from 'rxjs'

import {
  ADDRESS_LOCATION_RADIUS,
  LineString,
  LngLatCoords,
  Location,
  LocationType,
  Polygon,
} from '@ti/contracts'
import { Memoize } from '@ti/core/common/decorators'
import { ErrorHandler } from '@ti/core/common/services'
import { injectDestroy$ } from '@ti/core/common/utils'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { DEFAULT_GEOFENCE_COLOR, GeofenceStyles } from '@ti/shared/map/constants'
import {
  Coordinates,
  GeofenceColor,
  GeofenceFeature,
  MapGeofenceDataSource,
  MapGeofenceEditDataSource,
} from '@ti/shared/map/contracts'
import {
  metersToStoredCorridorWidth,
  storedCorridorWidthToMeters,
} from '@ti/shared/map/utils/corridor'

export abstract class BaseLocationGeofenceDataSource extends MapGeofenceDataSource {
  /**
   * Whether the `isHidden` flag removes geofences from `data$`. On by default: a
   * geofence the user hid stays off every map that draws the fleet's locations.
   */
  protected readonly appliesHiddenFlag: boolean = true

  public locationToFeature(location: Location): GeofenceFeature {
    const shapeProps: Partial<GeofenceFeature['properties']> = {}
    let geometry = location.geolocation as Polygon
    if (location.type === LocationType.Corridor) {
      if (location.geolocation?.type !== 'LineString' || !location.properties?.width) {
        console.warn(`Wrong corridor location data structure`, location)
      }
      shapeProps.isCorridor = true
      shapeProps.path = ((location.geolocation as LineString)?.coordinates ?? []) as LngLatCoords[]
      const width = location.properties?.width
      shapeProps.widthMeters = width ? storedCorridorWidthToMeters(width) : undefined
      geometry = { type: 'Polygon', coordinates: [] }
    } else if (location.type !== LocationType.Polygon) {
      if (location.properties?.center && location.properties.radius) {
        shapeProps.isCircle = true
        shapeProps.center = location.properties.center as Coordinates
        shapeProps.radiusInKm = location.properties.radius
      } else {
        console.warn(`Wrong circle location data structure`, location)
      }
    }

    const color = (location.color as GeofenceColor) || DEFAULT_GEOFENCE_COLOR
    return {
      id: location.id,
      type: 'Feature',
      properties: {
        id: location.id,
        name: location.name,
        type: location.type,
        address: location.address,
        color,
        isHidden: location.isHidden,
        // Style is used to assign color dynamically in the layer
        style: GeofenceStyles[color],
        ...shapeProps,
      },
      geometry,
    }
  }

  public featureToLocation(feature: GeofenceFeature): Location {
    if (feature.properties.isCorridor) {
      return {
        ...this.featureToBaseLocation(feature),
        type: LocationType.Corridor,
        geolocation: { type: 'LineString', coordinates: feature.properties.path ?? [] },
        properties: {
          width: feature.properties.widthMeters
            ? metersToStoredCorridorWidth(feature.properties.widthMeters)
            : undefined,
        },
      }
    }

    let type =
      feature.properties.type ??
      (feature.properties.address && feature.properties.radiusInKm === ADDRESS_LOCATION_RADIUS
        ? LocationType.Address
        : feature.properties.isCircle
          ? LocationType.Circle
          : LocationType.Polygon)

    // Check radius to match the Address
    if (
      type === LocationType.Address &&
      feature.properties.radiusInKm !== ADDRESS_LOCATION_RADIUS
    ) {
      type = LocationType.Circle
    }

    return {
      ...this.featureToBaseLocation(feature),
      type: type,
      geolocation: feature.geometry,
      properties: feature.properties.isCircle
        ? { center: feature.properties.center, radius: feature.properties.radiusInKm }
        : {},
    }
  }

  protected featureToBaseLocation(
    feature: GeofenceFeature,
  ): Omit<Location, 'type' | 'geolocation' | 'properties'> {
    return {
      id: feature.properties.id,
      name: feature.properties.name ?? '',
      color: feature.properties.color,
      address: feature.properties.address || '',
      isHidden: feature.properties.isHidden ?? false,
    }
  }

  protected applyHiddenFlag(features: GeofenceFeature[]): GeofenceFeature[] {
    return this.appliesHiddenFlag
      ? features.filter((feature) => !feature.properties.isHidden)
      : features
  }
}

export abstract class BaseLocationGeofenceEditDataSource
  extends BaseLocationGeofenceDataSource
  implements MapGeofenceEditDataSource
{
  protected readonly errorHandler = inject(ErrorHandler)
  protected readonly messageService = inject(MessageService)
  protected readonly languageService = inject(LanguageService)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly destroy$ = injectDestroy$()

  @Memoize()
  public override get allData$(): Observable<GeofenceFeature[]> {
    return combineLatest([this.getItems$(), this.dataFilterCb$]).pipe(
      map(([locations, dataFilterCb]) => {
        let data = locations.map((data) => this.locationToFeature(data))
        if (dataFilterCb) {
          data = dataFilterCb(data)
        }
        return data
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  @Memoize()
  public get data$(): Observable<GeofenceFeature[]> {
    return this.allData$.pipe(map((features) => this.applyHiddenFlag(features)))
  }

  abstract getItems$(): Observable<Location[]>

  abstract addGeofence(data: GeofenceFeature): Promise<GeofenceFeature | undefined>

  abstract deleteGeofence(data: GeofenceFeature): unknown

  abstract updateGeofence(data: GeofenceFeature): Promise<GeofenceFeature | undefined>

  protected async confirmDelete(feature: GeofenceFeature) {
    const labels = await this.languageService.massTranslate(
      {
        summary: 'locations.delete-dialog.summary',
        description: 'locations.delete-dialog.description',
        confirmButton: 'common.yes-delete',
        cancelButton: 'button.cancel',
      },
      { name: feature.properties.name },
    )
    return this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
  }

  protected showSavedSuccessfullyMessage(location: Location) {
    const messagePromise =
      location.type === LocationType.Address
        ? this.languageService.translate('locations.address-created-message.detail')
        : this.languageService.translate('locations.geofence-created-message.detail')

    messagePromise.then((message) => {
      this.messageService.add({ detail: message, life: 5000, severity: 'success' })
    })
  }

  protected showDeletedSuccessfullyMessage() {
    const messagePromise = this.languageService.translate(
      'locations.geofence-deleted-message.detail',
    )

    messagePromise.then((message) => {
      this.messageService.add({ detail: message, life: 5000, severity: 'success' })
    })
  }
}

/**
 * Draws exactly the geofences it is handed via `setLocations()`.
 *
 * It ignores the `isHidden` flag on purpose: the host already decided what belongs
 * on the map — the geofences attached to an alert, or the single location behind a
 * report row — and hiding a location only takes it off the fleet's own maps. An
 * alert still fires on a hidden geofence, so the map has to keep showing it.
 */
@Injectable()
export class StaticLocationGeofenceDataSource extends BaseLocationGeofenceDataSource {
  protected override readonly appliesHiddenFlag = false

  protected readonly items$ = new BehaviorSubject<GeofenceFeature[]>([])
  public readonly data$ = this.items$.asObservable()

  public setLocations(data: Location[]) {
    this.items$.next(data.map((item) => this.locationToFeature(item)))
  }
}
